import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../utils/apiClient';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../components/ToastNotification';
import '../styles/AdminPage.css';
import '../styles/AdminPageExtensions.css';

const AdminPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const mountedRef = useRef(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState({});
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [apiError, setApiError] = useState(null);

  // New item/box forms with proper validation
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    rarity: 'common',
    value: '',
    category: 'jewelry',
    imageUrl: '',
    sellBackRate: '0.70'
  });

  const [newBox, setNewBox] = useState({
    name: '',
    description: '',
    price: '',
    category: 'jewelry',
    imageUrl: '',
    items: []
  });

  // Controlled inputs for box item addition
  const [selectedItemId, setSelectedItemId] = useState('');
  const [dropRate, setDropRate] = useState('');

  // Validation states
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (user?.isAdmin) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    if (!mountedRef.current) return;
    
    try {
      const [statsRes, usersRes, itemsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(),
        adminAPI.getItems()
      ]);

      if (mountedRef.current) {
        setStats(statsRes.data);
        setUsers(usersRes.data.users || []);
        setItems(itemsRes.data.items || []);
        setApiError(null);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      
      if (mountedRef.current) {
        setApiError('API temporarily unavailable - showing demo data');
        toast.warning('API temporarily unavailable - showing demo data');
        
        // Fallback data for MVP
        setStats({
          totalUsers: 2,
          totalBoxesOpened: 287,
          totalRevenue: 2500.00,
          activeUsers: 2,
          adminUser: user?.email || 'admin@test.com'
        });
        setUsers([
          { id: 1, email: 'demo@test.com', role: 'user', balance: 25.00, lastLogin: '2025-08-10T12:00:00.000Z', isAdmin: false, isActive: true },
          { id: 2, email: 'admin@test.com', role: 'admin', balance: 100.00, lastLogin: '2025-08-10T12:00:00.000Z', isAdmin: true, isActive: true }
        ]);
        setItems([
          { id: 1, name: 'Silver Ring', rarity: 'common', value: 5.00, category: 'jewelry' },
          { id: 2, name: 'Gold Bracelet', rarity: 'rare', value: 15.00, category: 'jewelry' },
          { id: 3, name: 'Diamond Necklace', rarity: 'epic', value: 35.00, category: 'jewelry' }
        ]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const validateItemForm = (item) => {
    const errors = {};
    
    if (!item.name.trim()) errors.name = 'Name is required';
    if (!item.value || parseFloat(item.value) <= 0) errors.value = 'Value must be greater than 0';
    if (!item.sellBackRate || parseFloat(item.sellBackRate) < 0.01 || parseFloat(item.sellBackRate) > 1.00) {
      errors.sellBackRate = 'Sell back rate must be between 0.01 and 1.00';
    }
    
    return errors;
  };

  const validateBoxForm = (box) => {
    const errors = {};
    
    if (!box.name.trim()) errors.name = 'Name is required';
    if (!box.description.trim()) errors.description = 'Description is required';
    if (!box.price || parseFloat(box.price) <= 0) errors.price = 'Price must be greater than 0';
    if (box.items.length === 0) errors.items = 'At least one item is required';
    
    const totalDropRate = box.items.reduce((sum, item) => sum + item.dropRate, 0);
    if (totalDropRate > 1.0) errors.dropRate = 'Total drop rate cannot exceed 100%';
    
    return errors;
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    
    // Convert string values to numbers
    const itemData = {
      ...newItem,
      value: parseFloat(newItem.value),
      sellBackRate: parseFloat(newItem.sellBackRate)
    };
    
    const errors = validateItemForm(itemData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please fix form errors before submitting');
      return;
    }
    
    setTabLoading(prev => ({ ...prev, items: true }));
    
    try {
      await adminAPI.createItem(itemData);
      toast.success('Item created successfully!');
      setNewItem({
        name: '',
        description: '',
        rarity: 'common',
        value: '',
        category: 'jewelry',
        imageUrl: '',
        sellBackRate: '0.70'
      });
      setFormErrors({});
      fetchData();
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error('Failed to create item');
    } finally {
      setTabLoading(prev => ({ ...prev, items: false }));
    }
  };

  const handleCreateBox = async (e) => {
    e.preventDefault();
    
    // Convert string values to numbers
    const boxData = {
      ...newBox,
      price: parseFloat(newBox.price),
      items: newBox.items.map(item => ({
        ...item,
        itemId: item.itemId.toString(), // Ensure string for UUID compatibility
        dropRate: parseFloat(item.dropRate)
      }))
    };
    
    const errors = validateBoxForm(boxData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please fix form errors before submitting');
      return;
    }
    
    setTabLoading(prev => ({ ...prev, boxes: true }));
    
    try {
      await adminAPI.createBox(boxData);
      toast.success('Mystery box created successfully!');
      setNewBox({
        name: '',
        description: '',
        price: '',
        category: 'jewelry',
        imageUrl: '',
        items: []
      });
      setFormErrors({});
      fetchData();
    } catch (error) {
      console.error('Error creating box:', error);
      if (error.response?.status === 404) {
        toast.error('Box creation not available in demo mode');
      } else {
        toast.error('Failed to create box');
      }
    } finally {
      setTabLoading(prev => ({ ...prev, boxes: false }));
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    setTabLoading(prev => ({ ...prev, users: true }));
    
    try {
      await adminAPI.updateUser(userId, updates);
      toast.success('User updated successfully!');
      fetchData();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setTabLoading(prev => ({ ...prev, users: false }));
    }
  };

  const addItemToBox = () => {
    if (!selectedItemId || !dropRate) {
      toast.warning('Please select an item and enter a drop rate');
      return;
    }
    
    const numericDropRate = parseFloat(dropRate);
    if (numericDropRate < 0.0001 || numericDropRate > 1.0000) {
      toast.error('Drop rate must be between 0.0001 and 1.0000');
      return;
    }
    
    if (newBox.items.find(i => i.itemId === selectedItemId)) {
      toast.warning('Item already added to this box');
      return;
    }
    
    const currentTotal = newBox.items.reduce((sum, item) => sum + item.dropRate, 0);
    if (currentTotal + numericDropRate > 1.0) {
      toast.error('Adding this item would exceed 100% total drop rate');
      return;
    }
    
    const item = { itemId: selectedItemId, dropRate: numericDropRate };
    setNewBox({
      ...newBox,
      items: [...newBox.items, item]
    });
    
    // Clear form
    setSelectedItemId('');
    setDropRate('');
    toast.success('Item added to box');
  };

  const removeItemFromBox = (itemId) => {
    setNewBox({
      ...newBox,
      items: newBox.items.filter(item => item.itemId !== itemId)
    });
    toast.info('Item removed from box');
  };

  if (!user?.isAdmin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Loading admin dashboard..." />;
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <p>Manage your mystery box platform</p>
          {apiError && (
            <div className="api-error-banner">
              <span>⚠️ {apiError}</span>
            </div>
          )}
        </div>

        <div className="admin-tabs">
          <button 
            className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Dashboard
          </button>
          <button 
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            👥 Users
          </button>
          <button 
            className={`tab ${activeTab === 'items' ? 'active' : ''}`}
            onClick={() => setActiveTab('items')}
          >
            💎 Items
          </button>
          <button 
            className={`tab ${activeTab === 'boxes' ? 'active' : ''}`}
            onClick={() => setActiveTab('boxes')}
          >
            📦 Boxes
          </button>
        </div>

        <div className="admin-content">
          {activeTab === 'dashboard' && (
            <div className="dashboard-tab">
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>Total Users</h3>
                  <div className="stat-value">{stats?.totalUsers || 0}</div>
                </div>
                <div className="stat-card">
                  <h3>Total Revenue</h3>
                  <div className="stat-value">${parseFloat(stats?.totalRevenue || 0).toFixed(2)}</div>
                </div>
                <div className="stat-card">
                  <h3>Credits in Circulation</h3>
                  <div className="stat-value">${parseFloat(stats?.totalCreditsInCirculation || 0).toFixed(2)}</div>
                </div>
                <div className="stat-card">
                  <h3>Boxes Opened</h3>
                  <div className="stat-value">{stats?.openedBoxesCount || 0}</div>
                </div>
              </div>

              <div className="recent-activity">
                <h2>Recent Transactions</h2>
                <div className="transactions-list">
                  {stats?.recentTransactions?.map((transaction) => (
                    <div key={transaction.id} className="transaction-item">
                      <div className="transaction-user">{transaction.User.email}</div>
                      <div className="transaction-type">{transaction.type}</div>
                      <div className="transaction-amount">
                        ${parseFloat(transaction.amount).toFixed(2)}
                      </div>
                      <div className="transaction-date">
                        {new Date(transaction.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="users-tab">
              <h2>User Management</h2>
              <div className="users-table">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Balance</th>
                      <th>Admin</th>
                      <th>Active</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userData) => (
                      <tr key={userData.id}>
                        <td>{userData.email}</td>
                        <td>${parseFloat(userData.balance).toFixed(2)}</td>
                        <td>{userData.isAdmin ? '✅' : '❌'}</td>
                        <td>{userData.isActive ? '✅' : '❌'}</td>
                        <td>{userData.lastLogin ? new Date(userData.lastLogin).toLocaleDateString() : 'Never'}</td>
                        <td>
                          <button 
                            onClick={() => handleUpdateUser(userData.id, { isAdmin: !userData.isAdmin })}
                            className="admin-btn"
                          >
                            {userData.isAdmin ? 'Remove Admin' : 'Make Admin'}
                          </button>
                          <button 
                            onClick={() => handleUpdateUser(userData.id, { isActive: !userData.isActive })}
                            className="status-btn"
                          >
                            {userData.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'items' && (
            <div className="items-tab">
              <h2>Create New Item</h2>
              <form onSubmit={handleCreateItem} className="admin-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={newItem.name}
                      onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                      className={formErrors.name ? 'error' : ''}
                      required
                    />
                    {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={newItem.category}
                      onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                    >
                      <option value="jewelry">Jewelry</option>
                      <option value="shoes">Shoes</option>
                      <option value="streetwear">Streetwear</option>
                      <option value="electronics">Electronics</option>
                      <option value="accessories">Accessories</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Rarity</label>
                    <select
                      value={newItem.rarity}
                      onChange={(e) => setNewItem({...newItem, rarity: e.target.value})}
                    >
                      <option value="common">Common</option>
                      <option value="uncommon">Uncommon</option>
                      <option value="rare">Rare</option>
                      <option value="epic">Epic</option>
                      <option value="legendary">Legendary</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Value ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newItem.value}
                      onChange={(e) => setNewItem({...newItem, value: e.target.value})}
                      className={formErrors.value ? 'error' : ''}
                      required
                    />
                    {formErrors.value && <span className="error-text">{formErrors.value}</span>}
                  </div>
                  <div className="form-group">
                    <label>Sell Back Rate (0.01-1.00)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max="1.00"
                      value={newItem.sellBackRate}
                      onChange={(e) => setNewItem({...newItem, sellBackRate: e.target.value})}
                      className={formErrors.sellBackRate ? 'error' : ''}
                      required
                    />
                    {formErrors.sellBackRate && <span className="error-text">{formErrors.sellBackRate}</span>}
                  </div>
                  <div className="form-group">
                    <label>Image URL</label>
                    <input
                      type="url"
                      value={newItem.imageUrl}
                      onChange={(e) => setNewItem({...newItem, imageUrl: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                    rows="3"
                  />
                </div>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={tabLoading.items}
                >
                  {tabLoading.items ? 'Creating...' : 'Create Item'}
                </button>
              </form>

              <div className="existing-items">
                <h3>Existing Items ({items.length})</h3>
                <div className="items-grid">
                  {items.map((item) => (
                    <div key={item.id} className="item-preview">
                      <h4>{item.name}</h4>
                      <p>Category: {item.category}</p>
                      <p>Rarity: {item.rarity}</p>
                      <p>Value: ${parseFloat(item.value).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'boxes' && (
            <div className="boxes-tab">
              <h2>Create New Mystery Box</h2>
              {apiError && (
                <div className="demo-notice">
                  <p>⚠️ Box creation not available in demo mode</p>
                </div>
              )}
              <form onSubmit={handleCreateBox} className="admin-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={newBox.name}
                      onChange={(e) => setNewBox({...newBox, name: e.target.value})}
                      className={formErrors.name ? 'error' : ''}
                      required
                    />
                    {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={newBox.category}
                      onChange={(e) => setNewBox({...newBox, category: e.target.value})}
                    >
                      <option value="jewelry">Jewelry</option>
                      <option value="shoes">Shoes</option>
                      <option value="streetwear">Streetwear</option>
                      <option value="electronics">Electronics</option>
                      <option value="accessories">Accessories</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={newBox.price}
                      onChange={(e) => setNewBox({...newBox, price: e.target.value})}
                      className={formErrors.price ? 'error' : ''}
                      required
                    />
                    {formErrors.price && <span className="error-text">{formErrors.price}</span>}
                  </div>
                  <div className="form-group">
                    <label>Image URL</label>
                    <input
                      type="url"
                      value={newBox.imageUrl}
                      onChange={(e) => setNewBox({...newBox, imageUrl: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={newBox.description}
                    onChange={(e) => setNewBox({...newBox, description: e.target.value})}
                    className={formErrors.description ? 'error' : ''}
                    rows="3"
                    required
                  />
                  {formErrors.description && <span className="error-text">{formErrors.description}</span>}
                </div>

                <div className="box-items-section">
                  <h3>Box Items</h3>
                  <div className="add-item-controls">
                    <select 
                      value={selectedItemId}
                      onChange={(e) => setSelectedItemId(e.target.value)}
                    >
                      <option value="">Select an item</option>
                      {items.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} ({item.rarity})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.0001"
                      min="0.0001"
                      max="1.0000"
                      placeholder="Drop rate (0.0001-1.0000)"
                      value={dropRate}
                      onChange={(e) => setDropRate(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={addItemToBox}
                      disabled={!selectedItemId || !dropRate}
                    >
                      Add Item
                    </button>
                  </div>

                  <div className="box-items-list">
                    {newBox.items.map((boxItem, index) => {
                      const item = items.find(i => i.id.toString() === boxItem.itemId.toString());
                      return (
                        <div key={index} className="box-item">
                          <span>{item?.name || 'Unknown Item'}</span>
                          <span>{(boxItem.dropRate * 100).toFixed(2)}%</span>
                          <button
                            type="button"
                            onClick={() => removeItemFromBox(boxItem.itemId)}
                            className="remove-btn"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="drop-rate-total">
                    <span>Total Drop Rate: {(newBox.items.reduce((sum, item) => sum + item.dropRate, 0) * 100).toFixed(2)}%</span>
                    {newBox.items.reduce((sum, item) => sum + item.dropRate, 0) > 1.0 && (
                      <span className="error-text"> (Exceeds 100%)</span>
                    )}
                  </div>
                  {formErrors.items && <span className="error-text">{formErrors.items}</span>}
                  {formErrors.dropRate && <span className="error-text">{formErrors.dropRate}</span>}
                </div>

                <button 
                  type="submit" 
                  className="submit-btn" 
                  disabled={newBox.items.length === 0 || tabLoading.boxes || newBox.items.reduce((sum, item) => sum + item.dropRate, 0) > 1.0}
                >
                  {tabLoading.boxes ? 'Creating...' : 'Create Mystery Box'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
